import type React from 'react';
import { Container, Row, Col, Button, Spinner, Dropdown } from 'react-bootstrap';
import { useEffect, useMemo, useRef, useState } from 'react';
import MetricCard from '../../components/SystemAnalytics/MetricCard';
import systemAnalyticsIcon from '../../assets/icon/systemAnalyticsIcon.svg';
import requestIcon from '../../assets/icon/requestIcon.svg';
import userIcon from '../../assets/icon/userIcon.svg';
import rpsIcon from '../../assets/icon/rpsIcon.svg';
import rateIcon from '../../assets/icon/rateIcon.svg';
import cprIcon from '../../assets/icon/cprIcon.svg';
import filterIcon from '../../assets/icon/filterIcon.svg';
import { ReactComponent as ExportIcon } from '../../assets/icon/Export.svg';
import FilterForm from '../../components/SystemAnalytics/FilterForm';
import { useAzureData } from '../../context/AzureContext';
import { RefreshCcw, Search } from 'lucide-react';
import RegionWiseChart from '../../components/HealthAnalytic/RegionWiseChart';
import EnvironmentWiseChart from '../../components/HealthAnalytic/EnvironmentWiseChart';
import HealthChart from '../../components/HealthAnalytic/HealthChart';
import {
  fetchEndpointStatus,
  fetchHealthKpis,
  fetchHealthTimeline,
  refreshAllEnv,
  refreshSingleEnv,
  refreshSpecificEnv
} from '../../service/apiServices';
import EndpointStatusTable from '../../components/HealthAnalytic/EndpointStatusTable';
import UnileverAnimation from '../../components/UnileverAnimation';
import useCloudProvider from '../../hooks/useCloudProvider';
import { analyticsSheetBuilders, exportAnalyticsReport } from '../../utilities/analyticsExport';

const getDefaultDateRange = () => {
  const today = new Date();
  const pastDate = new Date();
  // pastDate.setMonth(today.getMonth() - 3); // 3 months before today
  pastDate.setDate(today.getDate() - 7); // 7 days before today
  return { fromDate: pastDate, toDate: today };
};

const truncateText = (text: string, maxLength = 30) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

interface CardsData {
  metricsData: {
    title: string;
    value: string;
    subtitle: string;
  }[];
}

interface DropdownValues {
  filters: {
    applications: string[];
    environments: string[];
    model_names: string[];
  };
}

const modelTypeData = [
  {
    modelProvider: 'Databricks',
    modelType: [
      { label: 'databricks', value: 'databricks' },
      { label: 'databricks_phi2', value: 'databricks_phi2' },
      { label: 'databricks_dbrx', value: 'databricks_dbrx' },
      { label: 'databricks_unl_aif', value: 'databricks_unl_aif' }
    ]
  },
  {
    modelProvider: 'OpenAI',
    modelType: [{ label: 'openai', value: 'openai' }]
  },
  {
    modelProvider: 'Gemini',
    modelType: [{ label: 'google_cloud_vertex_ai', value: 'google_cloud_vertex_ai' }]
  }
];

const endpointStatusTableHeaders = [
  {
    id: 0,
    name: 'Environment',
    type: 'environment',
    sortingAllowed: true
  },
  {
    id: 1,
    name: 'Model Provider',
    type: 'model_provider',
    sortingAllowed: true
  },
  {
    id: 2,
    name: 'Model',
    type: 'model',
    sortingAllowed: true
  },
  {
    id: 3,
    name: 'Served Entity Region',
    type: 'served_entity_region',
    sortingAllowed: true
  },
  {
    id: 4,
    name: 'Status',
    type: 'status_code',
    sortingAllowed: true
  },
  {
    id: 5,
    name: 'Response Summary',
    type: 'response_summary',
    sortingAllowed: false
  },
  {
    id: 6,
    name: 'Risk',
    type: 'risk',
    sortingAllowed: true
  },
  {
    id: 7,
    name: 'Last Data Refreshed At',
    type: 'last_data_refreshed_at',
    sortingAllowed: true
  }
];

const HealthAnalytics: React.FC = () => {
  const { apiToken, tempAdmin } = useAzureData();
  const [show, setShow] = useState(false);
  const { fromDate, toDate } = getDefaultDateRange();
  //eslint-disable-next-line
  const [dateRange, setDateRange] = useState({
    from: fromDate?.toLocaleDateString('en-GB'),
    to: toDate?.toLocaleDateString('en-GB')
  });
  const [cardsData, setCardsData] = useState<CardsData | null>(null);
  const [timelineData, setTimelineData] = useState<any>(null);
  const [endpointStatusData, setEndpointStatusData] = useState<any>(null);
  //eslint-disable-next-line
  const [filterDropdownValues, setFilterDropdownvalues] = useState<DropdownValues | null>(null);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [loading3, setLoading3] = useState(false);
  const [choosedFilter, setChoosedFilter] = useState<any>('');
  const [currentchoosedModel, setCurrentChoosedModel] = useState<any>('');
  const [selectedTimelineEnv, setSelectedTimelineEnv] = useState(false);
  const [search, setSearch] = useState('');
  const hasFetchedRef = useRef(false);
  const { selectCloud, selectedCloud, cloudProvidersList } = useCloudProvider();
  const [cloudFilterID, setCloudFilter] = useState(0);
  const selectedCloudName =
    cloudProvidersList.find((item) => item.id === selectedCloud)?.name || 'All Cloud Providers';

  const fetchKpiData = async () => {
    setLoading(true);
    try {
      let response = await fetchHealthKpis(apiToken, choosedFilter, cloudFilterID);
      response && setCardsData(response);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimelineData = async () => {
    setLoading2(true);
    try {
      let response = await fetchHealthTimeline(apiToken, choosedFilter, cloudFilterID);
      response && setTimelineData(response);
      setLoading2(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading2(false);
    }
  };

  const getEndpointStatus = async () => {
    setLoading3(true);
    try {
      let response = await fetchEndpointStatus(apiToken, choosedFilter, cloudFilterID);
      response && setEndpointStatusData(response);
      setLoading3(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading3(false);
    }
  };

  const fetchTimelineDataByEnv = async (env: any) => {
    setLoading2(true);
    try {
      let response = await fetchHealthTimeline(
        apiToken,
        { env: env !== 'all' ? env : '' },
        cloudFilterID
      );
      response && setTimelineData(response);
      setLoading2(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading2(false);
    }
  };

  const fetchRefreshAllEnv = async () => {
    setLoading3(true);
    try {
      let response = await refreshAllEnv('', apiToken);
      response && getEndpointStatus();
      setLoading3(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading3(false);
    }
  };

  const fetchRefreshSpecificEnv = async (env: any) => {
    setLoading3(true);
    try {
      let response = await refreshSpecificEnv({ env: env }, apiToken);
      response && getEndpointStatus();
      setLoading3(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading3(false);
    }
  };

  const fetchRefreshSingleEnv = async (env: any, id: any) => {
    setLoading3(true);
    try {
      let response = await refreshSingleEnv({ env: env, endpoint_group_id: id }, apiToken);
      response && getEndpointStatus();
      setLoading3(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading3(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef?.current) return;
    hasFetchedRef.current = true;
    fetchKpiData();
    fetchTimelineData();
    getEndpointStatus();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (choosedFilter || cloudFilterID) {
      fetchKpiData();
      fetchTimelineData();
      getEndpointStatus();
    }
    //eslint-disable-next-line
  }, [choosedFilter, cloudFilterID]);

  useEffect(() => {
    selectedTimelineEnv && fetchTimelineDataByEnv(selectedTimelineEnv);
    //eslint-disable-next-line
  }, [selectedTimelineEnv]);

  const metricsData = [
    {
      title: cardsData && cardsData?.metricsData[0]?.title,
      value: cardsData && cardsData?.metricsData[0]?.value,
      subtitle: cardsData && cardsData?.metricsData[0]?.subtitle,
      icon: requestIcon
    },
    {
      title: cardsData && cardsData?.metricsData[1]?.title,
      value: cardsData && cardsData?.metricsData[1]?.value,
      subtitle: cardsData && cardsData?.metricsData[1]?.subtitle,
      icon: userIcon
    },
    {
      title: cardsData && cardsData?.metricsData[2]?.title,
      value: cardsData && cardsData?.metricsData[2]?.value,
      subtitle: cardsData && cardsData?.metricsData[2]?.subtitle,
      icon: rpsIcon
    },
    {
      title: cardsData && cardsData?.metricsData[3]?.title,
      value: cardsData && cardsData?.metricsData[3]?.value,
      subtitle: cardsData && cardsData?.metricsData[3]?.subtitle,
      icon: rateIcon
    },
    {
      title: cardsData && cardsData?.metricsData[4]?.title,
      value: cardsData && cardsData?.metricsData[4]?.value,
      subtitle: cardsData && cardsData?.metricsData[4]?.subtitle,
      icon: cprIcon
    }
  ];

  // const filterModelType = currentchoosedModel ? modelType?.filter((data) => data.modelProvider === currentchoosedModel ) : []
  const filterModelType = useMemo(() => {
    return currentchoosedModel
      ? modelTypeData?.filter((data) => data.modelProvider === currentchoosedModel)
      : [];
    //eslint-disable-next-line
  }, [currentchoosedModel]);

  const filteredTableData = !search
    ? endpointStatusData?.data
    : endpointStatusData?.data?.filter((row: any) => {
        const searchTerm = search.toLowerCase();

        return (
          row.environment?.toLowerCase().includes(searchTerm) ||
          row.model_provider?.toLowerCase().includes(searchTerm) ||
          row.model?.toLowerCase().includes(searchTerm) ||
          row.served_entity_region?.toLowerCase().includes(searchTerm) ||
          row.response_summary?.toLowerCase().includes(searchTerm) ||
          row.risk?.toLowerCase().includes(searchTerm)
        );
      });

  const handleRefresh = (type: any) => {
    if (type === 'all') {
      fetchRefreshAllEnv();
    } else {
      fetchRefreshSpecificEnv(type);
    }
  };

  const handleExport = () => {
    exportAnalyticsReport({
      fileName: `api_health_analytics_${choosedFilter?.application || 'all_apps'}_${
        choosedFilter?.modelType || 'all_model_types'
      }_${choosedFilter?.env || 'all_env'}`,
      sheets: [
        {
          name: 'Filters',
          rows: [
            { Field: 'Model Provider', Value: choosedFilter?.application || 'All Model Providers' },
            { Field: 'Model Type', Value: choosedFilter?.modelType || 'All Model Types' },
            { Field: 'Environment', Value: choosedFilter?.env || 'All Environments' },
            { Field: 'Cloud Provider', Value: selectedCloudName },
            { Field: 'Date From', Value: choosedFilter?.timePeriod?.from || dateRange.from },
            { Field: 'Date To', Value: choosedFilter?.timePeriod?.to || dateRange.to }
          ]
        },
        {
          name: 'KPI Cards',
          rows: metricsData?.map((metric) => ({
            Title: metric.title,
            Value: metric.value,
            Subtitle: metric.subtitle
          }))
        },
        {
          name: 'Region Wise',
          rows: analyticsSheetBuilders.toSeriesRows(cardsData?.regionWiseBarChartData)
        },
        {
          name: 'Environment Wise',
          rows: analyticsSheetBuilders.toSeriesRows(cardsData?.envWiseStatusCodeBarChartData)
        },
        {
          name: 'Health Timeline',
          rows: analyticsSheetBuilders.healthTimelineRows(timelineData)
        },
        {
          name: 'Endpoint Status',
          rows: analyticsSheetBuilders.endpointStatusRows(filteredTableData || [])
        }
      ].filter((sheet) => sheet.rows && sheet.rows.length > 0)
    });
  };

  return (
    <>
      <Container fluid className="p-4 systemAnalytics_container">
        {/* Header */}
        <div className="mb-2">
          <Row className="justify-content-between align-items-start">
            <Col>
              <h2 className="analytics_heading m-0">
                <img src={systemAnalyticsIcon} alt="analytics" /> API Health Analytics
              </h2>
              <p className="analytics_subheading">
                Monitor endpoint uptime, regional performance and API reliability acrosss
                environments.
              </p>
            </Col>
          </Row>
        </div>

        {loading ? (
          <div
            className="d-flex justify-content-center"
            style={{ marginTop: '2%', marginBottom: '5%' }}>
            {/* <Spinner animation="border" variant="primary" /> */}
            <UnileverAnimation />
          </div>
        ) : (
          <>
            {/* Filter section */}
            <Row className="d-flex mb-4">
              <Col className="d-flex align-align-items-center">
                <div className="d-flex align-items-center show_date_btn me-2">
                  {truncateText(choosedFilter?.application) || 'All Model Provider'}
                </div>
                <div className="d-flex align-items-center show_date_btn me-2">
                  {truncateText(choosedFilter?.modelType) || 'All Model Type'}
                </div>
                {
                  <div className="d-flex align-items-center show_date_btn me-2">
                    {['Azure', 'GCP']?.[cloudFilterID - 1] || 'GCP and Azure'}
                  </div>
                }
                <div className="d-flex align-items-center show_date_btn me-2">
                  {truncateText(choosedFilter?.env) || 'All Environment'}
                </div>
              </Col>
              <Col className="d-flex justify-content-end">
                <Button
                  onClick={() => setShow(true)}
                  variant="outline-secondary"
                  className="d-flex align-items-center ms-2 gap-2 filterBtn">
                  <img src={filterIcon} alt="filter" />
                  Filter
                </Button>
                <Button
                  onClick={handleExport}
                  variant="outline-secondary"
                  className="d-flex align-items-center ms-2 gap-2 filterBtn"
                  disabled={loading || loading2 || loading3 || !cardsData || !timelineData}>
                  <ExportIcon width={16} height={16} />
                  Export
                </Button>
              </Col>
            </Row>

            {/* Metrics Cards */}
            <Row className="g-3 d-flex flex-wrap mb-4">
              {metricsData.map((metric, index) => (
                <Col className="" style={{ minWidth: '250px' }} key={index}>
                  <MetricCard
                    title={metric.title}
                    value={metric.value}
                    subtitle={metric.subtitle}
                    icon={metric.icon}
                  />
                </Col>
              ))}
            </Row>

            {/* Charts Row 1 */}
            <Row className="g-4 mb-4">
              <Col xs={12} lg={6}>
                {/* @ts-ignore */}
                <RegionWiseChart BarChartData={cardsData?.regionWiseBarChartData} />
              </Col>
              <Col xs={12} lg={6}>
                {/* @ts-ignore */}
                <EnvironmentWiseChart
                  MultiBarChartData={cardsData?.envWiseStatusCodeBarChartData}
                />
              </Col>
            </Row>
          </>
        )}

        {loading2 ? (
          <div
            className="d-flex justify-content-center"
            style={{ marginTop: '2%', marginBottom: '5%' }}>
            {/* <Spinner animation="border" variant="primary" /> */}
            <UnileverAnimation />
          </div>
        ) : (
          <>
            {/* Charts Row 2 */}
            <Row className="g-4 mb-4">
              <Col xs={12} lg={12}>
                {/* @ts-ignore */}
                <HealthChart
                  HealthChartData={timelineData && timelineData}
                  setSelectedTimelineEnv={setSelectedTimelineEnv}
                  selectedTimelineEnv={selectedTimelineEnv}
                />
              </Col>
            </Row>
          </>
        )}

        {/* Table */}
        <Row className="g-4 mb-4 timelineTable">
          <Col xs={12} lg={12}>
            {loading3 ? (
              <div
                className="d-flex justify-content-center"
                style={{ marginTop: '2%', marginBottom: '5%' }}>
                {/* <Spinner animation="border" variant="primary" /> */}
                <UnileverAnimation />
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <p className="h4 w400 my-2">Endpoint Status Overview </p>
                  </div>
                  <div className="d-flex align-items-center">
                    {tempAdmin && (
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="outline-primary"
                          id="dropdown-refresh"
                          className="me-2"
                          style={{ height: '37px', fontSize: '14px' }}>
                          <RefreshCcw size={16} /> Refresh
                        </Dropdown.Toggle>

                        <Dropdown.Menu style={{ fontSize: '14px' }}>
                          <Dropdown.Item onClick={() => handleRefresh('all')}>
                            Refresh All Environments
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleRefresh('prod')}>
                            Refresh Prod Environment
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleRefresh('qa')}>
                            Refresh QA Environment
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleRefresh('dev')}>
                            Refresh Dev Environment
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                    <div
                      className="search-box d-flex align-items-center"
                      style={{ height: '38px' }}>
                      <Search size={18} strokeWidth={1.5} color="#838FB4" />
                      <input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                {filteredTableData?.length > 0 ? (
                  <EndpointStatusTable
                    header={endpointStatusTableHeaders}
                    data={filteredTableData}
                    itemOptions={[5, 10]}
                    fetchRefreshSingleEnv={fetchRefreshSingleEnv}
                  />
                ) : (
                  <p className="text-center mt-3">No matching results found.</p>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <FilterForm
        show={show}
        onClose={() => {
          setShow(false);
        }}
        onApply={(filters) => {
          // fetchData(filters)
          setChoosedFilter(filters);
          setCloudFilter(selectedCloud);
        }}
        showCloudProvider={true}
        selectedCloud={selectedCloud}
        cloudProvidersList={cloudProvidersList}
        selectCloud={selectCloud}
        onDateRangeChange={setDateRange}
        onCurrentChoosedAppChange={setCurrentChoosedModel}
        fields={[
          {
            key: 'application',
            label: 'Model Type',
            type: 'select',
            options: [
              { value: 'Databricks', label: 'Databricks' },
              { value: 'OpenAI', label: 'OpenAI' },
              { value: 'Gemini', label: 'Gemini' }
            ]
          },
          {
            key: 'modelType',
            label: 'Model Provider',
            type: 'select',
            options: filterModelType[0]?.modelType
          },
          {
            key: 'env',
            label: 'Environment',
            type: 'select',
            options: [
              { value: 'dev', label: 'Dev' },
              { value: 'qa', label: 'Qa' },
              { value: 'prod', label: 'Prod' }
            ]
          }
        ]}
      />
    </>
  );
};

export default HealthAnalytics;
